import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPaperPlane, faPlus, faSpinner, faSync } from '@fortawesome/free-solid-svg-icons';
import NewsletterService from '../../services/NewsletterService';
import { Loading } from '../Loading';
import ConfirmationModal from '../ConfirmationModal';
import Modal from '../ModalConversion';
import { NewsletterNovedadesForm } from './NewsletterNovedadesForm';
import { NewsletterPreviewList } from './NewsletterPreviewList';

/**
 * El modulo tiene una sola configuracion: si no existe se crea, y una vez creada
 * siempre se edita. Por eso no hay listado de configuraciones ni boton de borrar.
 */
export function NewsletterAdminPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [sendConfirm, setSendConfirm] = useState(false);
  // Vista previa: se elige el canal porque el boletin es por canal.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewChannel, setPreviewChannel] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await NewsletterService.getConfig();
      setConfig(res.data || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const flash = (msg) => { setFeedback(msg); setTimeout(() => setFeedback(''), 4000); };

  const handleSave = async (data) => {
    setSaving(true);
    try {
      if (config) {
        await NewsletterService.updateConfig(data);
        flash('Configuración actualizada');
      } else {
        await NewsletterService.createConfig(data);
        flash('Configuración creada');
      }
      setFormOpen(false);
      await fetch();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    // Si la configuracion no tiene canales no hay nada que previsualizar.
    const configured = (config && config.channels_config) || [];
    if (configured.length === 0) {
      setError('La configuración no tiene canales. Agrega al menos uno para ver la vista previa.');
      return;
    }
    setPreviewChannel(configured[0].channel_alias || '');
    setPreviewOpen(true);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await NewsletterService.sendNow();
      const d = res.data || {};
      flash(`Enviado a ${d.sent || 0} canal(es), ${d.titles || 0} títulos`);
      await fetch();
      setSendConfirm(false);
    } catch (e) {
      setError(e.message);
      setSendConfirm(false);
    } finally {
      setSending(false);
    }
  };

  const isNew = !config;

  return (
    <div className="container-fluid px-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="text-white mb-0">Newsletter - Novedades a canales</h4>
        <div className="d-flex gap-2">
          <button className="btn btn-ink-outline" onClick={fetch} disabled={loading || sending}>
            <FontAwesomeIcon icon={faSync} className={loading ? 'fa-spin' : ''} /> Actualizar
          </button>
          {!isNew && (
            <>
              <button className="btn btn-ink-outline" onClick={handlePreview} disabled={sending}>
                <FontAwesomeIcon icon={faEye} className="me-1" /> Vista previa
              </button>
              <button
                className="btn btn-ink-outline"
                onClick={() => setSendConfirm(true)}
                disabled={sending}
              >
                {sending
                  ? <><FontAwesomeIcon icon={faSpinner} spin className="me-1" /> Enviando...</>
                  : <><FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Enviar ahora</>}
              </button>
              <button className="btn btn-primary" onClick={() => setFormOpen(true)} disabled={sending}>Editar configuración</button>
            </>
          )}
          {isNew && (
            <button className="btn btn-primary" onClick={() => setFormOpen(true)}>
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Crear configuración
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {feedback && <div className="alert alert-success">{feedback}</div>}

      {loading ? (
        <Loading />
      ) : (
        <ConfigSummary config={config} />
      )}

      <Modal isOpen={formOpen} onClose={() => setFormOpen(false)} size="xl">
        <h5 className="text-white mb-3">{isNew ? 'Nueva configuración' : 'Editar configuración'}</h5>
        <NewsletterNovedadesForm
          initial={config}
          isNew={isNew}
          onSubmit={handleSave}
          saving={saving}
          onCancel={() => setFormOpen(false)}
        />
      </Modal>

      <ConfirmationModal
        isOpen={sendConfirm}
        title="Enviar novedades"
        message="Se generará el PDF y se enviará a los canales configurados. ¿Continuar?"
        type="info"
        confirmText="Enviar"
        cancelText="Cancelar"
        isLoading={sending}
        onConfirm={handleSend}
        onClose={() => setSendConfirm(false)}
      />

      <NewsletterPreviewList
        isOpen={previewOpen}
        config={config}
        channel={previewChannel}
        channels={(config && config.channels_config) || []}
        onChannelChange={setPreviewChannel}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
}

function ConfigSummary({ config }) {
  if (!config) return null;
  const rows = [
    ['Título PDF', config.title_pdf],
    ['Periodo', `${config.period_qty} ${config.period_type === 'days' ? 'días' : 'meses'}`],
    ['Top de títulos', config.top_n],
    ['Categorías', (config.main_categories || []).join(', ') || '—'],
    ['Estado', config.active ? 'Activo' : 'Inactivo'],
    ['Último envío', config.last_run_at || '—'],
    ['Siguiente envío', config.next_run_at || '—'],
  ];
  return (
    <div className="modern-table-container">
      <table className="modern-table striped-table">
        <thead>
          <tr><th style={{ width: 220 }}>Campo</th><th>Valor</th></tr>
        </thead>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td className="fw-bold text-white">{k}</td>
              <td>{String(v)}</td>
            </tr>
          ))}
          <tr>
            <td className="fw-bold text-white">Canales</td>
            <td>
              {(config.channels_config || []).length === 0
                ? '—'
                : (config.channels_config || []).map(c => `${c.channel_alias} (${c.recipient_email})`).join(', ')}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}